import React, { useEffect, useRef } from 'react';
import { Tooltip } from '@mui/material';
import styles from './shared.module.css';

/**
 * Two-tier table header — a centred group label over one or more columns,
 * e.g. "Recommendations" over By/Type/Rating/…, or a strategy code over its
 * Min %/Median %/Max %. The leaf columns underneath keep their own alignment.
 *
 * A column joins a group through its `group` key. Consecutive columns with the
 * same key merge under one group cell, so spans are never counted by hand. A
 * column with no group spans both header rows on its own.
 *
 * DataTable supports this directly (`ColumnDef.group` + `columnGroups`). A
 * hand-written table uses `layoutHeader`, `useGroupHeaderRow` and
 * `GroupHeaderCell` — see TradeSuggestionsTable.
 */
export interface ColumnGroup {
  key: string;
  label: React.ReactNode;
  tooltip?: string;
}

export interface GroupableColumn {
  key: string;
  group?: string;
}

export type TopHeaderCell<C> =
  | { kind: 'column'; column: C; index: number }
  | { kind: 'group'; key: string; group: ColumnGroup; span: number };

export interface HeaderLayout<C> {
  /** False when no column has a group — render a single header row as before. */
  grouped: boolean;
  /** Row 1: ungrouped columns (rowSpan 2) and group cells (colSpan n), in column order. */
  top: TopHeaderCell<C>[];
  /** Row 2: the grouped leaf columns, in column order. */
  sub: { column: C; index: number }[];
}

export function layoutHeader<C extends GroupableColumn>(
  columns: C[],
  groups: ColumnGroup[] = [],
): HeaderLayout<C> {
  const grouped = columns.some(col => col.group);
  if (!grouped) {
    return { grouped, top: columns.map((column, index) => ({ kind: 'column', column, index })), sub: [] };
  }

  const byKey = new Map(groups.map(g => [g.key, g]));
  const top: TopHeaderCell<C>[] = [];
  const sub: HeaderLayout<C>['sub'] = [];

  columns.forEach((column, index) => {
    if (!column.group) {
      top.push({ kind: 'column', column, index });
      return;
    }
    sub.push({ column, index });
    const last = top[top.length - 1];
    if (last?.kind === 'group' && last.group.key === column.group) {
      last.span += 1;
    } else {
      top.push({
        kind: 'group',
        // The same group can appear twice if its columns are not adjacent.
        key: `${column.group}-${index}`,
        group: byKey.get(column.group) ?? { key: column.group, label: column.group },
        span: 1,
      });
    }
  });

  return { grouped, top, sub };
}

/**
 * The sub-header row sticks just below the group row. That row's height comes
 * from font metrics, so it is measured and published on the table as
 * `--group-head-h`, which `.sub-header-row th` in shared.module.css reads.
 */
export function useGroupHeaderRow(enabled: boolean) {
  const tableRef = useRef<HTMLTableElement>(null);
  const groupRowRef = useRef<HTMLTableRowElement>(null);

  useEffect(() => {
    const rowEl = groupRowRef.current;
    const tableEl = tableRef.current;
    if (!enabled || !rowEl || !tableEl) return undefined;
    const applyHeight = () => {
      tableEl.style.setProperty('--group-head-h', `${rowEl.getBoundingClientRect().height}px`);
    };
    applyHeight();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(applyHeight);
    observer.observe(rowEl);
    return () => observer.disconnect();
  }, [enabled]);

  return { tableRef, groupRowRef };
}

export const GroupHeaderCell: React.FC<{ group: ColumnGroup; span: number; style?: React.CSSProperties }> = ({
  group,
  span,
  style,
}) => (
  <th colSpan={span} className={styles['th-group']} style={style}>
    {group.tooltip ? (
      <Tooltip title={group.tooltip} arrow placement="top">
        <span>{group.label}</span>
      </Tooltip>
    ) : group.label}
  </th>
);
